import { lazy, Suspense, Component, useEffect } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { AuthProvider } from '@/components/auth/AuthProvider'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { TooltipProvider } from '@/components/ui/tooltip'
import { UpdatePrompt } from '@/components/UpdatePrompt'
import { NotificationBanner } from '@/components/NotificationBanner'
import { NetworkStatusBanner } from '@/components/NetworkStatusBanner'
import { OfflineErrorState } from '@/components/OfflineErrorState'
import { PostHogIdentify } from '@/components/PostHogIdentify'
import { analytics } from '@/lib/analytics'

const Landing = lazy(() => import('@/pages/Landing'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Settings = lazy(() => import('@/pages/Settings'))
const NotesPage = lazy(() => import('@/pages/notes/NotesPage'))
const FormsPage = lazy(() => import('@/pages/forms/FormsPage'))
const FormBuilder = lazy(() => import('@/pages/forms/FormBuilder'))
const FormResponses = lazy(() => import('@/pages/forms/FormResponses'))
const FormAnalytics = lazy(() => import('@/pages/forms/FormAnalytics'))
const PollsPage = lazy(() => import('@/pages/polls/PollsPage'))
const PollBuilder = lazy(() => import('@/pages/polls/PollBuilder'))
const FeedbackPage = lazy(() => import('@/pages/feedback/FeedbackPage'))
const FeedbackBuilder = lazy(() => import('@/pages/feedback/FeedbackBuilder'))
const FeedbackInbox = lazy(() => import('@/pages/feedback/FeedbackInbox'))
const SavedResponses = lazy(() => import('@/pages/SavedResponses'))
const Invites = lazy(() => import('@/pages/Invites'))
const Notifications = lazy(() => import('@/pages/Notifications'))
const PublicForm = lazy(() => import('@/pages/forms/PublicForm'))
const PublicPoll = lazy(() => import('@/pages/polls/PublicPoll'))
const PublicFeedback = lazy(() => import('@/pages/feedback/PublicFeedback'))
const LegalPage = lazy(() => import('@/pages/LegalPage'))
const PublicLegalPage = lazy(() => import('@/pages/PublicLegalPage'))
const Billing = lazy(() => import('@/pages/Billing'))
const Pricing = lazy(() => import('@/pages/Pricing'))
const McpConnect = lazy(() => import('@/pages/McpConnect'))

class ErrorBoundaryInner extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  render() {
    if (this.state.hasError) return <OfflineErrorState />
    return this.props.children
  }
}

function LocationResetBoundary({ children }: { children: ReactNode }) {
  const { key } = useLocation()
  return <ErrorBoundaryInner key={key}>{children}</ErrorBoundaryInner>
}

function PageTracker() {
  const location = useLocation()
  useEffect(() => {
    analytics.capture('$pageview', { $current_url: window.location.href })
  }, [location.pathname])
  return null
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PostHogIdentify />
        <TooltipProvider>
          <AnimatePresence mode="wait">
            <Suspense fallback={<PageLoader />}>
              <PageTracker />
              <LocationResetBoundary>
              <Routes>
                <Route path="/" element={<Landing />} />

                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="notes" element={<NotesPage />} />
                  <Route path="forms" element={<FormsPage />} />
                  <Route path="forms/:formId" element={<FormBuilder />} />
                  <Route path="forms/:formId/responses" element={<FormResponses />} />
                  <Route path="forms/:formId/analytics" element={<FormAnalytics />} />
                  <Route path="polls" element={<PollsPage />} />
                  <Route path="polls/:pollId" element={<PollBuilder />} />
                  <Route path="feedback" element={<FeedbackPage />} />
                  <Route path="feedback/:boxId" element={<FeedbackBuilder />} />
                  <Route path="feedback/:boxId/inbox" element={<FeedbackInbox />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="saved" element={<SavedResponses />} />
                  <Route path="invites" element={<Invites />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="legal/:page" element={<LegalPage />} />
                  <Route path="billing" element={<Billing />} />
                </Route>

                {/* Public routes */}
                <Route path="/mcp-connect" element={<McpConnect />} />
                <Route path="/legal/:page" element={<PublicLegalPage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/f/:formId" element={<PublicForm />} />
                <Route path="/p/:pollId" element={<PublicPoll />} />
                <Route path="/b/:boxId" element={<PublicFeedback />} />
              </Routes>
              </LocationResetBoundary>
            </Suspense>
          </AnimatePresence>
          <UpdatePrompt />
          <NotificationBanner />
          <NetworkStatusBanner />
        </TooltipProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
